import { useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { ShieldCheck, Building2, Timer } from "lucide-react";

const levels = [
  { id: "steward", label: "SIA Steward", rate: 14 },
  { id: "doorsupervisor", label: "Door Supervisor", rate: 19 },
  { id: "headguard", label: "Head Guard", rate: 24 },
];

export default function Calculator() {
  const [mode, setMode] = useState("guard");
  const [hours, setHours] = useState(28);
  const [level, setLevel] = useState("doorsupervisor");
  const [guards, setGuards] = useState(6);

  const rate = levels.find((l) => l.id === level).rate;
  const weekly = hours * rate;
  const fillTime = Math.max(4, Math.round(18 - guards * 0.9));

  return (
    <section data-testid="calculator-section" className="border-y border-white/5 bg-[#0a0d13] py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono-tag text-[11px] text-amber-500">Run the numbers</p>
          <h2 className="font-display mt-4 max-w-2xl text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl">
            See what fair looks like
          </h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex gap-2">
              <button
                data-testid="calc-tab-guard"
                onClick={() => setMode("guard")}
                className={`flex items-center gap-2 rounded-md px-5 py-3 font-display text-sm font-bold uppercase transition-all duration-200 ${
                  mode === "guard" ? "bg-amber-500 text-[#07090e]" : "border border-white/10 text-slate-400 hover:text-slate-50"
                }`}
              >
                <ShieldCheck className="h-4 w-4" /> I'm a guard
              </button>
              <button
                data-testid="calc-tab-venue"
                onClick={() => setMode("venue")}
                className={`flex items-center gap-2 rounded-md px-5 py-3 font-display text-sm font-bold uppercase transition-all duration-200 ${
                  mode === "venue" ? "bg-amber-500 text-[#07090e]" : "border border-white/10 text-slate-400 hover:text-slate-50"
                }`}
              >
                <Building2 className="h-4 w-4" /> I'm a venue
              </button>
            </div>

            {mode === "guard" ? (
              <div className="mt-10 space-y-9">
                <div>
                  <div className="mb-4 flex items-baseline justify-between">
                    <label className="font-mono-tag text-[10px] text-slate-400">Hours per week</label>
                    <span data-testid="calc-hours-value" className="font-display text-2xl font-extrabold text-slate-50">{hours}h</span>
                  </div>
                  <Slider data-testid="calc-hours-slider" value={[hours]} onValueChange={(v) => setHours(v[0])} min={4} max={60} step={1} />
                </div>
                <div>
                  <label className="font-mono-tag mb-4 block text-[10px] text-slate-400">License level</label>
                  <div className="flex flex-wrap gap-2">
                    {levels.map((l) => (
                      <button
                        key={l.id}
                        data-testid={`calc-level-${l.id}`}
                        onClick={() => setLevel(l.id)}
                        className={`rounded-md px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                          level === l.id ? "bg-amber-500/15 text-amber-500 ring-1 ring-amber-500/50" : "bg-white/5 text-slate-400 hover:text-slate-50"
                        }`}
                      >
                        {l.label} · £{l.rate}/hr
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-10">
                <div className="mb-4 flex items-baseline justify-between">
                  <label className="font-mono-tag text-[10px] text-slate-400">Guards needed tonight</label>
                  <span data-testid="calc-guards-value" className="font-display text-2xl font-extrabold text-slate-50">{guards}</span>
                </div>
                <Slider data-testid="calc-guards-slider" value={[guards]} onValueChange={(v) => setGuards(v[0])} min={1} max={40} step={1} />
                <p className="mt-6 text-sm leading-relaxed text-slate-500">
                  Post a shift with pay rate and dress code. Verified guards claim it in real time —
                  and because no-shows get banned, posted means covered.
                </p>
              </div>
            )}
          </div>

          <motion.div
            key={mode}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="glow-amber relative flex flex-col justify-center overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-br from-[#111520] to-[#0b0f16] p-10 lg:col-span-7"
          >
            <div className="bg-grid-tactical pointer-events-none absolute inset-0 opacity-40" />
            {mode === "guard" ? (
              <div className="relative">
                <p className="font-mono-tag text-[10px] text-amber-500">Estimated weekly earnings</p>
                <p data-testid="calc-earnings-result" className="font-display mt-3 text-7xl font-black leading-none text-slate-50 sm:text-8xl">
                  £{weekly.toLocaleString()}
                </p>
                <div className="mt-6 flex items-center gap-2 text-emerald-400">
                  <Timer className="h-4 w-4" />
                  <span className="text-sm font-medium">You keep 100% of your agreed rate — no agency cut</span>
                </div>
                <p className="mt-4 max-w-md text-sm text-slate-500">
                  Based on average {rate > 18 ? "licensed " : ""}guard rates on GuardGig28.
                  No platform fees deducted from your hourly rate.
                </p>
              </div>
            ) : (
              <div className="relative">
                <p className="font-mono-tag text-[10px] text-amber-500">Average time to fully staffed</p>
                <p data-testid="calc-filltime-result" className="font-display mt-3 text-7xl font-black leading-none text-slate-50 sm:text-8xl">
                  {fillTime}<span className="text-3xl text-slate-400"> min</span>
                </p>
                <div className="mt-6 flex items-center gap-2 text-emerald-400">
                  <ShieldCheck className="h-4 w-4" />
                  <span className="text-sm font-medium">Every guard SIA-licensed and background-checked</span>
                </div>
                <p className="mt-4 max-w-md text-sm text-slate-500">
                  Backed by a 99.8% attendance rate. If a guard ever no-shows, they're banned
                  and a replacement is dispatched automatically.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
